import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState("loading"); // loading | success | expired | error
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [resendMsg, setResendMsg] = useState("");
  const called = useRef(false);

  useEffect(() => {
    if (called.current) return;
    called.current = true;
    if (!token) {
      setState("error");
      setMessage("Lien de vérification invalide.");
      return;
    }
    api
      .post("/auth/verify-email", { token })
      .then((r) => {
        setState("success");
        setMessage(r.data.message);
      })
      .catch((err) => {
        const detail = err?.response?.data?.detail;
        if (detail === "expired") {
          setState("expired");
          setMessage("Ce lien a expiré (validité 24 h). Tu peux demander un nouveau lien ci-dessous.");
        } else {
          setState("error");
          setMessage(formatApiError(err, "Vérification impossible."));
        }
      });
  }, [token]);

  const handleResend = async (e) => {
    e.preventDefault();
    setResendMsg("");
    try {
      const { data } = await api.post("/auth/resend-verification", { email });
      setResendMsg(data.message);
    } catch (err) {
      setResendMsg(formatApiError(err));
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <Card className="w-full max-w-md border-slate-200 shadow-sm text-center">
        <CardContent className="pt-8 pb-8 space-y-4">
          {state === "loading" && <Loader2 className="w-10 h-10 text-teal-600 mx-auto animate-spin" data-testid="verify-loading" />}
          {state === "success" && (
            <>
              <CheckCircle2 className="w-12 h-12 text-teal-600 mx-auto" />
              <h2 className="font-heading text-xl font-semibold text-slate-900" data-testid="verify-success-title">Compte activé</h2>
              <p className="text-sm text-slate-600" data-testid="verify-success-message">{message}</p>
              <Link to="/connexion">
                <Button className="bg-teal-600 hover:bg-teal-700" data-testid="verify-go-login-button">Se connecter</Button>
              </Link>
            </>
          )}
          {(state === "expired" || state === "error") && (
            <>
              <XCircle className="w-12 h-12 text-orange-600 mx-auto" />
              <h2 className="font-heading text-xl font-semibold text-slate-900" data-testid="verify-error-title">Vérification impossible</h2>
              <p className="text-sm text-slate-600" data-testid="verify-error-message">{message}</p>
              {state === "expired" && (
                <form onSubmit={handleResend} className="space-y-3 text-left">
                  <div className="space-y-2">
                    <Label htmlFor="resend-email">Ton email</Label>
                    <Input id="resend-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="resend-email-input" />
                  </div>
                  <Button type="submit" className="w-full bg-amber-500 hover:bg-amber-600" data-testid="resend-submit-button">
                    Renvoyer un lien de vérification
                  </Button>
                  {resendMsg && (
                    <p className="text-sm text-teal-700 bg-teal-50 border border-teal-200 rounded-lg px-3 py-2" data-testid="resend-success-message">
                      {resendMsg}
                    </p>
                  )}
                </form>
              )}
              <Link to="/connexion" className="block text-sm text-teal-700 hover:underline" data-testid="verify-back-login-link">
                Retour à la connexion
              </Link>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
